// Share a Custom Ranking: the poster image (square / story) with its options,
// and, once the ranking is saved and public, its link. Built on the shared
// PosterSharePanel (the same panel My Top Movies uses) with the
// ShareImageOptions switches.
//
// A published ranking shares its own card (title + @username), drawn from its
// published version. A saved DRAFT shows the owner a private preview of the
// working version (title and @username included), fetched with their session
// from /rankings/me/:id/card.png — it never gets a URL anyone else could
// open, and there is no link to share until it is published. A guest's or an
// unsaved ranking uses the id-only preview card, which never carries
// user-written text, so the title and username switches are unavailable there.
//
// Two layouts: Poster Grid (the original image) and Compact List (a small
// poster beside every title, the whole ranking). Compact List is suggested for
// a Top 15 / 20 but never picked for the user. Switching keeps every choice
// the other layout can use; the grid's own choices (First 5 / Full list,
// hidden poster titles) wait unchanged for when it is picked again.
//
// Every option is part of the image URL and the server renders the image, so
// the preview is exactly the file that is downloaded or shared. Options are
// remembered per ranking on this device and never change the ranking itself:
// "Position numbers" starts from the ranking's public setting. The Footer
// message defaults to "Make your own Top N." with the saved titles' count.
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    RANKING_CARD_PARTS, RANKING_CARD_STYLE_OPTIONS, RANKING_PREVIEW_COUNT, isCompactListSuggested, isLongRanking,
    rankingCardFormatNote, rankingCardFormatsFor, rankingCardQuery, resolveRankingCardOptions,
} from '@aluvo/constants/src/rankings';
import { shareFooterOptions } from '@aluvo/constants/src/shareFooter';
import PosterSharePanel from '../PosterShare/PosterSharePanel';
import ShareImageOptions from '../PosterShare/ShareImageOptions';
import ShareLayoutPicker from '../PosterShare/ShareLayoutPicker';
import { RankingService } from '../../services/rankingService';
import { loadRankingCardOptions, saveRankingCardOptions } from '../../utils/rankingDraft';
import { ANALYTICS_EVENTS } from '../../analytics';
import { trackRanking } from '../../analytics/rankingAnalytics';

/**
 * @param {object[]} items
 * @param {number} size
 * @param {boolean} [showPositions=true]   the ranking's public setting (starts the numbers switch)
 * @param {object|null} [shared]           { slug, shareUrl, title, ownerUsername } for a published ranking
 * @param {object|null} [privateCard]      { id, title, ownerUsername } for the owner's saved draft (private preview)
 * @param {boolean} [isOwner]              the viewer owns the ranking (copy says "your")
 * @param {string} [storageId]             where to remember the image options (ranking id or slug)
 */
export default function RankingSharePanel({
    items, size, composition, showPositions = true, shared = null, privateCard = null, isOwner = true, storageId = null,
    loggedIn = false, surface = 'editor', noLinkHint,
}) {
    const optionsKey = storageId || shared?.slug || 'draft';
    const [options, setOptions] = useState(() => loadRankingCardOptions(optionsKey));
    useEffect(() => { setOptions(loadRankingCardOptions(optionsKey)); }, [optionsKey]);
    const change = useCallback((next) => {
        setOptions((prev) => {
            const value = typeof next === 'function' ? next(prev) : next;
            saveRankingCardOptions(optionsKey, value);
            return value;
        });
    }, [optionsKey]);

    const resolved = useMemo(() => resolveRankingCardOptions(options, { size, showPositions }), [options, size, showPositions]);
    const ownPreview = !shared?.slug && Boolean(privateCard?.id);
    const personal = Boolean(shared?.slug) || ownPreview;
    const query = rankingCardQuery(resolved, { personal });
    // For the private preview this is only a cache key (the items are in it so
    // an edit reloads it); the image itself is fetched with the session.
    const imageUrlFor = useCallback(() => {
        if (shared?.slug) return RankingService.sharedCardUrl(shared.slug, { query });
        if (ownPreview) return `private:${privateCard.id}?${query}&items=${items.map((i) => `${i.kind}:${i.tmdbId}`).join(',')}`;
        return RankingService.previewCardUrl({ items, size, query });
    }, [shared?.slug, ownPreview, privateCard?.id, items, size, query]);
    const loadImage = useMemo(() => (ownPreview ? () => RankingService.ownCardBlob(privateCard.id, { query }) : null), [ownPreview, privateCard?.id, query]);

    // One event for every share action; `action` says which (never the title).
    const onTrack = useCallback((kind, params) => {
        trackRanking(ANALYTICS_EVENTS.RANKING_SHARED, {
            action: kind,
            size,
            composition: composition || 'none',
            surface,
            logged_in: loggedIn,
            has_link: Boolean(shared?.shareUrl),
            ...params,
        }, { listSlug: shared?.slug || null });
    }, [size, composition, surface, loggedIn, shared?.shareUrl, shared?.slug]);

    const long = isLongRanking(size);
    const list = resolved.style === 'list';
    const ownerUsername = shared?.ownerUsername || privateCard?.ownerUsername || null;
    const parts = RANKING_CARD_PARTS.map((part) => {
        const value = resolved.show[part.key];
        if (part.key === 'title' || part.key === 'owner') {
            if (!personal) return { ...part, value, unavailable: 'Shown once the ranking is saved' };
            if (part.key === 'title' && ownPreview && !privateCard.title) return { ...part, value, unavailable: 'Give your ranking a title first' };
            if (part.key === 'owner') {
                if (!ownerUsername) return { ...part, value, unavailable: 'No username to show' };
                return { ...part, value, label: isOwner ? 'Your @username' : `@${ownerUsername}`, sub: `“@${ownerUsername}” under the title` };
            }
        }
        if (part.key === 'numbers') {
            const same = value === (showPositions !== false);
            const sub = list ? '1, 2, 3… before each title (the order is kept either way)' : part.sub;
            return { ...part, value, sub: `${sub}${same ? '' : ` · ${showPositions !== false ? 'shown' : 'hidden'} on the public page`}` };
        }
        if (part.key === 'captions' && list) {
            return { ...part, value: true, locked: 'Always shown in the Compact List — the titles are the list' };
        }
        return { ...part, value };
    });
    const layout = long && !list ? {
        value: resolved.layout,
        options: [
            { key: 'preview', label: `First ${RANKING_PREVIEW_COUNT}` },
            { key: 'full', label: `Full list (${size})` },
        ],
        hint: resolved.layout === 'full'
            ? `All ${size} titles as posters.`
            : `The first ${RANKING_PREVIEW_COUNT} of ${size}, with a note that the full list is on Aluvo.`,
    } : null;

    const suggested = isCompactListSuggested(size) ? 'list' : null;
    // Counts are the real ones: a draft with 7 of 20 picked never reads as 20.
    const picked = Math.min(items?.length ?? size, size);
    const footer = { value: resolved.footer, options: shareFooterOptions('ranking', { count: picked || size }) };
    const styleHint = list
        ? `${picked < size ? `The ${picked} of ${size} titles picked so far` : `All ${size} titles`}, ${picked > 10 ? 'in two columns read top to bottom' : 'each with a small poster'}.`
        : suggested && 'Compact List fits all your titles in one readable image.';

    const title = shared?.title || privateCard?.title || `My Top ${size}`;
    return (
        <PosterSharePanel
            imageUrlFor={imageUrlFor}
            format={resolved.format}
            onFormatChange={(format) => change((prev) => ({ ...prev, format }))}
            formats={rankingCardFormatsFor(size, resolved.layout, resolved.style)}
            formatNote={rankingCardFormatNote(size, resolved.layout, resolved.style)}
            before={(
                <ShareLayoutPicker
                    options={RANKING_CARD_STYLE_OPTIONS}
                    value={resolved.style}
                    onChange={(style) => change((prev) => ({ ...prev, style }))}
                    suggested={suggested}
                    hint={styleHint}
                />
            )}
            shareUrl={shared?.shareUrl || null}
            shareText={`${title} 🏆 Think you can make a better list?`}
            shareTitle={title}
            filenameFor={(format) => `aluvo-ranking-top-${size}${list ? '-list' : long ? `-${resolved.layout}` : ''}-${format}.png`}
            imageAlt={(format) => `${title} — Top ${size}${list ? ', compact list' : long && resolved.layout === 'preview' ? `, first ${RANKING_PREVIEW_COUNT} titles` : ''} as a ${format} image`}
            linkHint="Posted links show the ranking as the preview image. Links printed inside an image can't be tapped — share the link with it."
            noLinkHint={noLinkHint || 'Publish your ranking to get a link to share.'}
            onTrack={onTrack}
            loadImage={loadImage}
        >
            <ShareImageOptions
                parts={parts}
                onToggle={(key, value) => change((prev) => ({ ...prev, show: { ...prev.show, [key]: value } }))}
                layout={layout}
                layoutLabel="Posters shown"
                onLayoutChange={(key) => change((prev) => ({ ...prev, layout: key, ...(key === 'full' ? { format: 'story' } : {}) }))}
                footer={footer}
                onFooterChange={(key) => change((prev) => ({ ...prev, footer: key }))}
            />
        </PosterSharePanel>
    );
}
